import { ListResult } from "@/common/models";
import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import {
  ActionEnum,
  AxisType,
  CountOrderByEnum,
  CountParametersTier1Tier2,
  Direction,
  GroupByEnum,
  ScopedAxisInput,
  ScopedCountParameters,
  TimeAxisRangeInput,
  TimeAxisType,
  targetAxis,
} from "@/gql/types";
import { EVENT_FIELDS } from "@/components/modals/events-modal/use-events";
import { eventParser } from "@/gql/event-parser";
import { getEmailAddressFromExtendedData } from "@/gql/types";
import moment from "moment";

/**
 * Live queries for the Microsoft 365 Gen AI Governance dashboard, as intel-api
 * takes them (PBI 24967, Oct 2026). The AI relation carries the other axes in
 * its `scope`:
 *
 *   W1, W2  tier2count   w AiContentRelation, scope {x ObjectSeverity, y ContentLabel}
 *   W3, W4  tier2count   x AiContentRelation, scope {y ContentLabel}
 *           both read `files`, the files counted; `count` is events
 *   W5      tier1count   w EventType ai_interaction · x ContentLabel · y Employee,
 *                        RecordCount grouped by XValue on Y: employees per label
 *
 *   files of matching AI events   aiContentItems(filter: {coordinates: [the W1/W2 shape]})
 *   one event                      event(id:) — events are read with `events`
 *
 * Every period is explicit (`z: {type: Month, values: ["2026-10-01"]}`).
 */

export interface AxisCount {
  /** axis value — an info-type id, or a level number, depending on the query */
  value: string;
  count: number;
}

const rows_to_counts = (rows: any[]): AxisCount[] =>
  (rows || [])
    .filter(Boolean)
    .map((r) => ({
      value: (r.y && r.y.value) || "",
      count: Number(r.count) || 0,
    }))
    .filter((r) => r.value);

const value_of = (axis: any) => (axis && axis.value) || "";

/* ------------------------------------------------------------------ *
 * 1. Frames
 * ------------------------------------------------------------------ */

/** the shared frame moved back `subtract` periods, still as an explicit date */
export const shift_frame = (
  frame: TimeAxisRangeInput,
  subtract: number
): TimeAxisRangeInput => {
  const from = frame.values && frame.values[0];
  if (!from || !subtract) return frame;
  const unit = frame.type === TimeAxisType.Week ? "week" : "month";
  return {
    type: frame.type,
    values: [moment(from).subtract(subtract, unit).format("YYYY-MM-DD")],
  };
};

/** Levels as the backend numbers them. Severity and sensitivity share the scheme. */
export const LEVELS: { level: LevelKey; value: number }[] = [
  { level: "high", value: 3 },
  { level: "medium", value: 2 },
  { level: "low", value: 1 },
  { level: "none", value: 0 },
];

export type LevelKey = "high" | "medium" | "low" | "none";

/* ------------------------------------------------------------------ *
 * 2. W1 and W2 — risky activity by relation, information category and severity
 *
 * x names the six categories' L0 labels (data/categories.ts), not the virtual
 * labels: a risk card splits by severity, never by sensitivity. W5 keeps the
 * virtual labels.
 * ------------------------------------------------------------------ */

/**
 * A risk card's severity, inside the relation's scope: ObjectSeverity, the
 * content item's own severity, numbered as Severity is ("3" | "2" | "1"). All
 * three are asked for at once and the slider filters the cube locally.
 */
export const RISK_SEVERITY_AXIS = AxisType.ObjectSeverity;
export const RISK_SEVERITIES = ["3", "2", "1"];

/** one value as `value`, several as `values`, as intel-api's examples write them */
const axis = (type: AxisType, values: string[]) =>
  values.length === 1 ? cord(type, values) : cord3(type, values);

/**
 * Whether the content drill-downs list the files of matching AI events
 * (`items`, see §5) or the events list. There is no fallback: a failed request
 * is shown as one. Setting this to false turns every content list back to the
 * events list, deliberately and at once.
 */
export const AI_CONTENT_ITEMS_AVAILABLE = true;

/**
 * Rows per page for the list counts. The server pages at 100 by default and
 * says nothing when it stops: an ungrouped label axis came back with 100 of
 * its 145 rows. A load that hits this cap is logged.
 */
export const COUNT_PAGE_SIZE = 1000;

export interface RiskCell {
  /** the AiContentRelation code, e.g. "UploadTo_AI" */
  relation: string;
  /** the information category's L0 label */
  label: string;
  /** "3" | "2" | "1" on the Severity axis ("High" | "Medium" | "Low" on ObjectSeverity) */
  severity: string;
  count: number;
}

/** W1 / W2's params: the relations, scoped by severity and category */
export const risk_cells_params = ({
  relations,
  labels,
  frame,
}: {
  relations: string[];
  labels: string[];
  frame: TimeAxisRangeInput;
}): ScopedCountParameters => ({
  coordinate: {
    w: {
      ...axis(AxisType.AiContentRelation, relations),
      scope: {
        x: cord3(RISK_SEVERITY_AXIS, RISK_SEVERITIES),
        y: axis(AxisType.ContentLabel, labels),
      },
    } as ScopedAxisInput,
    z: frame,
  },
  action: ActionEnum.List,
});

export const get_risk_cells = gql2<
  { relations: string[]; labels: string[]; frame: TimeAxisRangeInput },
  RiskCell[]
>("ms-365-gen-ai.risk-cells", (input) => ({
  query: `{
    tier2count(params:${formatInput(risk_cells_params(input))}){
      w{value}
      x{value}
      y{value}
      files
    }
  }`,
  // w the relation, x the severity, y the category; the number is files —
  // `count` would be events, and the card counts files (Oct 2026)
  mapper: (res: any) =>
    (res.tier2count || [])
      .filter(Boolean)
      .map((r: any) => ({
        relation: value_of(r.w),
        severity: value_of(r.x),
        label: value_of(r.y),
        count: Number(r.files) || 0,
      })),
}));

/* ------------------------------------------------------------------ *
 * 3. W3 and W4 — AI activity by relation and content label
 * ------------------------------------------------------------------ */

export interface RelationCount {
  /** the backend's relation code, e.g. "AI_Read" */
  relation: string;
  /** the content label the row is for */
  label: string;
  count: number;
}

/**
 * Every AI relation against a set of content labels, for a period: the
 * relation on x, scoped by the labels on y. One call serves both cards: W3
 * keeps the UploadTo_AI rows, W4 the AI-originated ones.
 *
 * The labels are what the cards' slider stands at (Oct 2026): the six
 * information categories when nothing is narrowed, else one set of virtual
 * labels — Highly sensitive, Medium+ or Low+, one per category. Given none,
 * the label axis names nothing and the server groups by every label, which is
 * only for W3's expand, where the sub-labels are listed.
 */
export const label_rows_params = ({
  relations,
  labels,
  frame,
}: {
  relations: string[];
  labels?: string[];
  frame: TimeAxisRangeInput;
}): ScopedCountParameters => ({
  coordinate: {
    x: {
      ...axis(AxisType.AiContentRelation, relations),
      scope: {
        y: labels && labels.length ? axis(AxisType.ContentLabel, labels) : cord(AxisType.ContentLabel),
      },
    } as ScopedAxisInput,
    z: frame,
  },
  action: ActionEnum.List,
  pageSize: COUNT_PAGE_SIZE,
});

export const get_ai_label_rows = gql2<
  { relations: string[]; labels?: string[]; frame: TimeAxisRangeInput },
  RelationCount[]
>("ms-365-gen-ai.ai-label-rows", (input) => ({
  query: `{
    tier2count(params:${formatInput(label_rows_params(input))}){
      x{value}
      y{value}
      files
    }
  }`,
  mapper: (res: any) =>
    (res.tier2count || [])
      .filter(Boolean)
      .map((r: any) => ({
        relation: value_of(r.x),
        label: value_of(r.y),
        // files, not events (`count`), as the cards show (Oct 2026)
        count: Number(r.files) || 0,
      }))
      .filter((r: RelationCount) => r.relation && r.label),
}));

/* ------------------------------------------------------------------ *
 * 4. W5 — employees who interacted with AI, by virtual label
 * ------------------------------------------------------------------ */

export const AI_INTERACTION_EVENT = "ai_interaction";

/** the employees who interacted with AI under one label */
export interface InteractionCell {
  /** the category's label, or its virtual label at a slider stop */
  label: string;
  count: number;
}

/**
 * W5's counts: how many employees interacted with AI under each label —
 * `tier1count`, w EventType ai_interaction, x the labels, y Employee, counted
 * as records on Y and grouped by the X value, so each row is one label and its
 * employees (Michael's query, Oct 2026; it replaced y EmployeeType, which
 * counted employee types, not employees). The per-employee `tier2count` by
 * Employee is the drill-down's list, not this.
 */
export const interaction_counts_params = ({
  labels,
  frame,
}: {
  labels: string[];
  frame: TimeAxisRangeInput;
}): CountParametersTier1Tier2 => ({
  coordinate: {
    z: frame,
    w: cord(AxisType.EventType, [AI_INTERACTION_EVENT]),
    x: axis(AxisType.ContentLabel, labels),
    y: cord(AxisType.Employee),
  },
  page: 0,
  pageSize: COUNT_PAGE_SIZE,
  action: ActionEnum.RecordCount,
  groupBy: GroupByEnum.XValue,
  targetAxis: targetAxis.Y,
});

export const get_interaction_cells = gql2<
  { labels: string[]; frame: TimeAxisRangeInput },
  InteractionCell[]
>("ms-365-gen-ai.interaction-cells", (input) => ({
  query: `{
    tier1count(params:${formatInput(interaction_counts_params(input))}){
      count
      x{value}
    }
  }`,
  mapper: (res: any) =>
    (res.tier1count || [])
      .filter(Boolean)
      .map((r: any) => ({
        label: value_of(r.x),
        count: Number(r.count) || 0,
      })),
}));

export interface InteractionEmployee {
  /** the employee entity's id, which the events list filters on */
  id: string;
  name: string;
  email: string;
  department: string;
  /** their AI interactions under the row's labels */
  count: number;
  files: number;
  /** their first and last AI interaction in the period, as returned */
  first_event: string;
  last_event: string;
}

/**
 * W5's drill-down: the employees behind a row, most interactions first —
 * `tier2count` by Employee over the row's labels, as intel-api gives it (Oct
 * 2026). The employees are on y, so the list targets Y; x is the label filter.
 * Each row brings the person, their department and their first and last
 * interaction (`startDate` / `endDate`), so nothing is read per person.
 */
export const interaction_employees_params = ({
  labels,
  frame,
  cursor,
}: {
  labels: string[];
  frame: TimeAxisRangeInput;
  cursor: string;
}): CountParametersTier1Tier2 => ({
  coordinate: {
    z: frame,
    w: cord(AxisType.EventType, [AI_INTERACTION_EVENT]),
    x: cord3(AxisType.ContentLabel, labels),
    y: cord(AxisType.Employee),
  },
  cursor,
  action: ActionEnum.List,
  targetAxis: targetAxis.Y,
  direction: Direction.Descend,
  orderBy: CountOrderByEnum.Count,
});

export const get_interaction_employees = gql2<
  { labels: string[]; frame: TimeAxisRangeInput; cursor: string },
  ListResult<InteractionEmployee>
>("ms-365-gen-ai.interaction-employees", (input) => ({
  query: `{
    entities: tier2count(params:${formatInput(interaction_employees_params(input))}){
      cursor
      count
      files
      startDate
      endDate
      entities{
        id
        extendedData
        name
        email
        department
        externalId
        path
        ownerOrSite
        sourceApplication
        labels{ labelCategory name id }
      }
    }
  }`,
  mapper: (res: any) => {
    const rows = (res.entities || []).filter(Boolean);
    return {
      cursor: (rows[0] && rows[0].cursor) || "",
      objects: rows
        .map((r: any) => {
          const entity = (r.entities && r.entities[0]) || {};
          const email =
            entity.email || (entity.id ? getEmailAddressFromExtendedData(entity) : "") || "";
          return {
            id: entity.id || "",
            name: entity.name || email || entity.externalId || "",
            email: email || entity.externalId || "",
            department: entity.department || "",
            count: Number(r.count) || 0,
            files: Number(r.files) || 0,
            first_event: r.startDate || "",
            last_event: r.endDate || "",
          };
        })
        .filter((e: InteractionEmployee) => e.id),
    };
  },
}));

/* ------------------------------------------------------------------ *
 * 5. The content drill-downs — the files of matching AI events
 *
 * Every card that counts content opens the files behind its count
 * (`aiContentItems`, PBI 24967; intel-api Release-252), over the same scoped
 * coordinate as the risk counts:
 *
 *   Risky Employees' Activities   UploadTo_AI
 *   Risky AI Agent Activities     AI_Created, AI_Modified, AI_Read, or the clicked one
 *   Information Shared With AI    UploadTo_AI
 *   AI Activities matrix          the clicked row's relation
 *
 *   w  AiContentRelation, scope:
 *        x  ObjectSeverity   the risk levels the list shows ("0".."3")
 *        y  ContentLabel     the category's L0 label, or all six
 *   z  the selected month
 *
 * Each file carries who exposed it (`exposedBy`, the AI agent), who it was
 * exposed to (`exposedTo`, the employees) and its own `severity` (0-3); the
 * event it belongs to is `eventRefId`, read with `event(id:)` for details.
 * Sensitivity is the file's own, and the list filters on it. The employees
 * list behind Widget 5 is a different query and is not this.
 * ------------------------------------------------------------------ */

export interface ContentItemsFilter {
  /** AiContentRelation codes */
  relations: string[];
  /** the categories' L0 label ids */
  labels: string[];
  /** ObjectSeverity values, "0" (no risk) .. "3" */
  severities: string[];
  frame: TimeAxisRangeInput;
}

/** the `items` filter argument, as it is written into the query */
export const content_items_filter_input = ({
  relations,
  labels,
  severities,
  frame,
  cursor,
}: ContentItemsFilter & { cursor: string }) =>
  formatInput({
    coordinates: [
      {
        w: {
          ...axis(AxisType.AiContentRelation, relations),
          scope: {
            x: cord3(RISK_SEVERITY_AXIS, severities),
            y: axis(AxisType.ContentLabel, labels),
          },
        },
        z: frame,
      },
    ],
    cursor,
  });

export const get_ai_content_items = gql2<
  ContentItemsFilter & { cursor: string },
  ListResult<any>
>("ms-365-gen-ai.ai-content-items", (input) => ({
  query: `{
    #EXPORT
    aiContentItems(filter:${content_items_filter_input(input)}){
      id
      cursor
      eventRefId
      exposedBy{ id type name externalId }
      exposedTo{ id type name externalId }
      severity
      name
      subType
      category
      externalId
      clusterUID
      extendedProperties{ topic }
      labelHistory{ userId }
      archive{ archiveName siblings }
      internalItems{ name }
      labels{
        infoLabel{ id name type concreteScore generation confidence }
        ctxLabels{ id name type labelType labelCategory generation confidence }
        adhocLabels{ id name }
        piis{ id name labelCategory }
      }
      path
      sourceApplication
      sensitivity
      sensitivityLevel
      createdAt
      extendedData
    }
    #EXPORT_END
  }`,
  mapper: (res: any) => {
    const rows = (res.aiContentItems || []).filter(Boolean);
    // the same item id more than once is the data's to fix; the rows are kept
    const repeated = rows.length - new Set(rows.map((r: any) => r.id)).size;
    if (repeated) console.warn(`[gen-ai] aiContentItems: ${repeated} row(s) repeat an item id already on this page`);
    return {
      // the page's cursor rides on its rows, as on the events list
      cursor: (rows[0] && rows[0].cursor) || "",
      objects: rows,
    };
  },
}));

/**
 * One event by id, for a content item's detail. The singular `event(id:)`
 * query, per the spec — not `events(filter:{id:...})`, which it retires —
 * read with the same fields as the events list so it parses the same way.
 */
export const get_event_by_id = gql2<{ id: string }, any>(
  "ms-365-gen-ai.event-by-id",
  ({ id }) => ({
    query: `{
      #EXPORT
      event(id:${JSON.stringify(id)}){
${EVENT_FIELDS}      }
      #EXPORT_END
    }`,
    mapper: (res: any) => (res.event ? eventParser(res.event) : null),
  })
);

/* ------------------------------------------------------------------ *
 * 6. Every activity per category — the Visibility cards' pool
 *
 * The same grouping ms-365-activities counts by, with no AI axis. `labels`
 * are the L0 ids of the six category families (data/categories.ts), the ones
 * the cards' rows resolve to — a filter, not a raw-label list. The answer is
 * one count per category, at any sensitivity.
 * ------------------------------------------------------------------ */

export const get_events_by_category = gql2<
  { labels: string[]; frame: TimeAxisRangeInput },
  AxisCount[]
>("ms-365-gen-ai.events-by-category", ({ labels, frame }) => {
  // v1 for the explicit month, as get_ai_label_rows
  const params: CountParametersTier1Tier2 = {
    coordinate: {
      x: cord(AxisType.None),
      y: cord3(AxisType.ContentLabel, labels),
      z: frame,
    },
    action: ActionEnum.List,
  };
  return {
    query: `{
      tier1count(params:${formatInput(params)}){
        y{value}
        count
      }
    }`,
    mapper: (res: any) => rows_to_counts(res.tier1count),
  };
});

export type { TimeAxisRangeInput };
